import { useEffect, useState } from "react";
import { Alert, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { getAllPlans, createPlan, updatePlan, deletePlan } from "../apiHelper";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import ResponsiveTable from "../components/ResponsiveTable";
import {
  CreditCard,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  Users,
  Check,
  X,
} from "lucide-react";
import PlanModal from "../components/PlanModal";

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const { getAccessTokenSilently } = useAuth();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const result = await getAllPlans(token);

      if (result.success) {
        setPlans(result.data || []);
      } else {
        throw new Error(result.message || "Failed to load plans");
      }
    } catch (err) {
      setError(err.message || "Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlan = () => {
    setEditingPlan(null);
    setShowModal(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setShowModal(true);
  };

  const handleSavePlan = async (planData) => {
    try {
      setMessage(null);

      // Count current active plans
      const currentActivePlans = plans.filter((p) => p.isActive);
      const activeCount = currentActivePlans.length;

      // Validation for exactly 3 active plans rule
      if (editingPlan) {
        // Updating existing plan
        const wasActive = editingPlan.isActive;
        const willBeActive = planData.isActive;

        if (!wasActive && willBeActive) {
          // Activating a plan
          if (activeCount >= 3) {
            setMessage({
              type: "error",
              text: "Cannot activate plan. You must have exactly 3 active plans. Please deactivate another plan first.",
            });
            return;
          }
        } else if (wasActive && !willBeActive) {
          // Deactivating a plan
          if (activeCount <= 3) {
            setMessage({
              type: "error",
              text: "Cannot deactivate plan. You must maintain exactly 3 active plans. Please activate another plan first.",
            });
            return;
          }
        }
      } else {
        // Creating new plan
        if (planData.isActive) {
          if (activeCount >= 3) {
            setMessage({
              type: "error",
              text: "Cannot create active plan. You already have 3 active plans. Please deactivate one first or create this plan as inactive.",
            });
            return;
          }
        }
      }

      const token = await getAccessTokenSilently();

      let result;
      if (editingPlan) {
        // Update existing plan
        result = await updatePlan(token, editingPlan._id, planData);
      } else {
        // Create new plan
        result = await createPlan(token, planData);
      }

      if (result.success) {
        setMessage({
          type: "success",
          text: editingPlan
            ? "Plan updated successfully!"
            : "Plan created successfully!",
        });
        setShowModal(false);
        setEditingPlan(null);
        fetchPlans();
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: "error", text: result.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    }
  };

  const handleDeletePlan = async (planId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this plan? This will deactivate it."
      )
    ) {
      return;
    }

    try {
      setMessage(null);
      const token = await getAccessTokenSilently();
      const result = await deletePlan(token, planId);

      if (result.success) {
        setMessage({ type: "success", text: "Plan deleted successfully!" });
        fetchPlans();
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: "error", text: result.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-3 lg:p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center h-96">
          <div className="text-center">
            <CircularProgress size={48} />
            <Typography variant="h6" className="mt-4">
              Loading subscription plans...
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-3 lg:p-6">
        <div className="max-w-7xl mx-auto">
          <Alert severity="error">{error}</Alert>
        </div>
      </div>
    );
  }

  const columns = [
    {
      key: "name",
      label: "Plan Name",
      mobileLabel: "Plan",
      render: (plan) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
            {plan.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-medium flex items-center gap-2 flex-wrap">
              {plan.name}
              {plan.isPopular && (
                <Badge
                  variant="secondary"
                  className="bg-yellow-100 text-yellow-800"
                >
                  Popular
                </Badge>
              )}
            </div>
            {plan.description && (
              <div className="text-sm text-gray-500">{plan.description}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "price",
      label: "Price",
      mobileLabel: "Price",
      render: (plan) => (
        <div className="flex items-center">
          <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
          <span className="font-semibold">{plan.price.toFixed(2)}</span>
        </div>
      ),
    },
    {
      key: "billingCycle",
      label: "Billing Cycle",
      mobileLabel: "Billing",
      render: (plan) => (
        <Badge variant="secondary" className="bg-blue-100 text-blue-800">
          {plan.billingCycle}
        </Badge>
      ),
    },
    {
      key: "suggestedOrgSize",
      label: "Suggested Size",
      mobileLabel: "Size",
      render: (plan) => {
        return (
          <div className="flex items-center">
            <Users className="h-4 w-4 text-gray-400 mr-1" />
            <span>
              {plan.suggestedOrgSize ? (
                <span className="text-gray-900">{plan.suggestedOrgSize}</span>
              ) : (
                <span className="text-gray-400 italic">Not specified</span>
              )}
            </span>
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      mobileLabel: "Status",
      render: (plan) => (
        <Badge
          variant="secondary"
          className={
            plan.isActive
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }
        >
          {plan.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      mobileLabel: "Actions",
      render: (plan) => (
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleEditPlan(plan);
            }}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleDeletePlan(plan._id);
            }}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-3 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                Subscription Plans
              </h1>
              <p className="text-sm lg:text-base text-gray-600">
                Manage available subscription plans
              </p>
            </div>
          </div>
          <Button
            onClick={handleAddPlan}
            className="!bg-green-500 !hover:bg-green-600 !text-white font-semibold px-6 py-3"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Plan
          </Button>
        </div>

        {/* Message */}
        {message && (
          <Alert
            severity={message.type === "success" ? "success" : "error"}
            className="rounded-xl"
          >
            {message.text}
          </Alert>
        )}

        {/* Active Plans Requirement Alert */}
        {plans.filter((p) => p.isActive).length !== 3 && (
          <Alert severity="warning" className="rounded-xl">
            <strong>Important:</strong> You must maintain exactly 3 active
            plans. Currently you have{" "}
            <strong>{plans.filter((p) => p.isActive).length}</strong> active{" "}
            {plans.filter((p) => p.isActive).length === 1 ? "plan" : "plans"}.
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
            <CardHeader className="pb-2">
              <div className="flex flex-row items-center justify-between">
                <div className="text-sm font-medium">Total Plans</div>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{plans.length}</div>
              <p className="text-xs text-muted-foreground">All plans</p>
            </CardContent>
          </Card>

          <Card
            className={`border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300 ${
              plans.filter((p) => p.isActive).length === 3
                ? "ring-2 ring-green-500"
                : "ring-2 ring-yellow-500"
            }`}
          >
            <CardHeader className="pb-2">
              <div className="flex flex-row items-center justify-between">
                <div className="text-sm font-medium">Active Plans</div>
                <Check className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {plans.filter((p) => p.isActive).length}
                <span className="text-sm text-gray-500 ml-1">/ 3 required</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {plans.filter((p) => p.isActive).length === 3
                  ? "Perfect! ✓"
                  : "Must be exactly 3"}
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
            <CardHeader className="pb-2">
              <div className="flex flex-row items-center justify-between">
                <div className="text-sm font-medium">Inactive Plans</div>
                <X className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {plans.filter((p) => !p.isActive).length}
              </div>
              <p className="text-xs text-muted-foreground">Deactivated</p>
            </CardContent>
          </Card>
        </div>

        {/* Plans Table */}
        <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
          <CardHeader className="pb-4 lg:pb-6">
            <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <CreditCard className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
              </div>
              <span className="text-base lg:text-xl">All Plans</span>
            </CardTitle>
            <CardDescription>
              View and manage all subscription plans
            </CardDescription>
          </CardHeader>
          <CardContent>
            {plans.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6">
                <CreditCard className="h-16 w-16 text-gray-400 mb-6" />
                <h3 className="text-2xl font-semibold text-gray-700 mb-3">
                  No plans found
                </h3>
                <p className="text-base text-gray-500 mb-8 max-w-md text-center">
                  Create your first subscription plan to get started.
                </p>
                <Button
                  onClick={handleAddPlan}
                  className="!bg-green-500 !hover:bg-green-600 !text-white px-6 py-3 text-base font-semibold"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add First Plan
                </Button>
              </div>
            ) : (
              <ResponsiveTable
                columns={columns}
                data={plans}
                loading={false}
                emptyMessage="No subscription plans available."
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Plan Modal */}
      {showModal && (
        <PlanModal
          plan={editingPlan}
          currentPlans={plans}
          onClose={() => {
            setShowModal(false);
            setEditingPlan(null);
          }}
          onSave={handleSavePlan}
        />
      )}
    </div>
  );
};

export default Plans;
